import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Upload, Eye, Pencil, Trash2, RefreshCw, Download } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KINDS = { policy: "Policy", procedure: "Procedure", template: "Template" };
const ACCEPT = ".pdf,.docx,.txt,.md";

const CategoryPicker = ({ cats, value, onChange }) => {
  const all = value.includes("all");
  const toggle = (slug) => onChange(value.includes(slug) ? value.filter((v) => v !== slug) : [...value.filter((v) => v !== "all"), slug]);
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={all} onCheckedChange={(c) => onChange(c ? ["all"] : [])} data-testid="kb-cat-all" />All document types</label>
      {!all && <div className="grid max-h-48 grid-cols-2 gap-2 overflow-auto rounded-lg border p-3">{cats.map((c) => (
        <label key={c.slug} className="flex items-center gap-2 text-sm"><Checkbox checked={value.includes(c.slug)} onCheckedChange={() => toggle(c.slug)} data-testid={`kb-cat-${c.slug}`} />{c.name}</label>
      ))}</div>}
    </div>
  );
};

const MetaFields = ({ f, setF, cats }) => (
  <>
    <div className="space-y-1"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Working at Height Policy" data-testid="kb-title-input" /></div>
    <div className="space-y-1"><Label>Type</Label>
      <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="kb-kind-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`kb-kind-${k}`}>{l}</SelectItem>)}</SelectContent></Select></div>
    <div className="space-y-1"><Label>Use for</Label><CategoryPicker cats={cats} value={f.categories} onChange={(v) => setF({ ...f, categories: v })} /></div>
  </>
);

const UploadDialog = ({ open, setOpen, cats, done }) => {
  const [f, setF] = useState({ title: "", kind: "policy", categories: ["all"] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", f.title);
    fd.append("kind", f.kind);
    fd.append("categories", f.categories.join(","));
    setBusy(true);
    try {
      await api.post("/knowledge", fd);
      toast.success("Added to the Knowledge Base. New AI drafts will follow it.");
      setOpen(false);
      setFile(null);
      setF({ title: "", kind: "policy", categories: ["all"] });
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Upload to Knowledge Base</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1"><Label>File (PDF, Word, TXT or MD · max 8MB)</Label><Input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files[0] || null)} data-testid="kb-file-input" /></div>
        <MetaFields f={f} setF={setF} cats={cats} />
        <Button className="w-full" disabled={!file || busy || !f.categories.length} onClick={submit} data-testid="kb-upload-submit">{busy ? "Reading file…" : "Upload"}</Button>
      </div>
    </DialogContent></Dialog>
  );
};

const EditDialog = ({ item, close, cats, done }) => {
  const [f, setF] = useState({ title: item.title, kind: item.kind, categories: item.categories });
  const save = async () => {
    try {
      await api.patch(`/knowledge/${item.id}`, f);
      toast.success("Saved");
      close();
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Edit Knowledge Base file</DialogTitle></DialogHeader>
      <div className="space-y-4"><MetaFields f={f} setF={setF} cats={cats} /><Button className="w-full" disabled={!f.categories.length || f.title.length < 2} onClick={save} data-testid="kb-edit-save">Save changes</Button></div>
    </DialogContent></Dialog>
  );
};

const PreviewDialog = ({ id, close }) => {
  const [k, setK] = useState(null);
  useEffect(() => {
    api.get(`/knowledge/${id}`).then((r) => setK(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  return (
    <Dialog open onOpenChange={(o) => !o && close()}><DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden"><DialogHeader><DialogTitle>{k?.title || "Loading…"}</DialogTitle></DialogHeader>
      {k && <p className="-mt-2 text-xs text-muted-foreground">{k.filename} · {k.chars.toLocaleString()} characters read by Saflr AI</p>}
      <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border bg-muted/40 p-4 font-sans text-sm" data-testid="kb-preview-text">{k?.text}</pre>
    </DialogContent></Dialog>
  );
};

const Row = ({ k, cats, canManage, onPreview, onEdit, reload }) => {
  const fileRef = useRef(null);
  const names = k.categories.includes("all") ? "All document types" : k.categories.map((s) => cats.find((c) => c.slug === s)?.name || s).join(", ");
  const act = (p, msg) => p.then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(errMsg(e)));
  const replace = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    act(api.post(`/knowledge/${k.id}/replace`, fd), "File replaced. New drafts will use the updated version.");
    e.target.value = "";
  };
  return (
    <li className={`flex flex-wrap items-center gap-3 py-4 transition-opacity ${k.active ? "" : "opacity-60"}`} data-testid={`kb-item-${k.id}`}>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium"><span className="rounded-full bg-health/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-health">{KINDS[k.kind]}</span>{k.title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{names} · {k.filename} · updated {new Date(k.updated_at).toLocaleDateString("en-GB")} by {k.uploaded_by}</p>
      </div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground">{k.active ? "Active" : "Off"}<Switch checked={k.active} disabled={!canManage} onCheckedChange={(v) => act(api.patch(`/knowledge/${k.id}`, { active: v }), v ? "Active — AI drafts will use it" : "Turned off")} data-testid={`kb-active-${k.id}`} /></label>
      <Button size="sm" variant="ghost" onClick={onPreview} aria-label="Preview" data-testid={`kb-preview-${k.id}`}><Eye className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" onClick={() => download(`/knowledge/${k.id}/file`, k.filename).catch((e) => toast.error(errMsg(e)))} aria-label="Download" data-testid={`kb-download-${k.id}`}><Download className="h-4 w-4" /></Button>
      {canManage && <>
        <Button size="sm" variant="ghost" onClick={onEdit} aria-label="Edit" data-testid={`kb-edit-${k.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => fileRef.current.click()} aria-label="Replace file" data-testid={`kb-replace-${k.id}`}><RefreshCw className="h-4 w-4" /></Button>
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={replace} data-testid={`kb-replace-input-${k.id}`} />
        <Button size="sm" variant="ghost" onClick={() => window.confirm(`Delete "${k.title}"?`) && act(api.delete(`/knowledge/${k.id}`), "Deleted")} aria-label="Delete" data-testid={`kb-delete-${k.id}`}><Trash2 className="h-4 w-4" /></Button>
      </>}
    </li>
  );
};

export default function KnowledgeBase() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [upload, setUpload] = useState(false);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/knowledge").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  if (!data) return <Loader label="Loading Knowledge Base" />;
  const active = data.items.filter((k) => k.active).length;
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="knowledge-base-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Your approved material</p><h1 className="mt-2 text-3xl font-bold">Knowledge Base</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Upload your approved policies, procedures and templates. Saflr AI follows the active files that match each document type when it writes a new draft.</p></div>
        {data.can_manage && <Button onClick={() => setUpload(true)} data-testid="kb-upload-open"><Upload className="mr-2 h-4 w-4" />Upload file</Button>}
      </div>
      {!data.items.length ? <EmptyState icon={BookOpen} title="No files yet" text={data.can_manage ? "Upload your first policy, procedure or template so AI drafts match how your company works." : "Ask an admin or HSE manager to upload your company policies."} /> : (
        <div className="rounded-2xl border bg-card p-6">
          <p className="text-sm text-muted-foreground" data-testid="kb-summary">{active} of {data.items.length} files active · up to 6 matching files are used per draft (most recently updated first)</p>
          <ul className="mt-2 divide-y">{data.items.map((k) => <Row key={k.id} k={k} cats={cats} canManage={data.can_manage} onPreview={() => setPreview(k.id)} onEdit={() => setEdit(k)} reload={load} />)}</ul>
        </div>
      )}
      <UploadDialog open={upload} setOpen={setUpload} cats={cats} done={load} />
      {edit && <EditDialog item={edit} close={() => setEdit(null)} cats={cats} done={load} />}
      {preview && <PreviewDialog id={preview} close={() => setPreview(null)} />}
    </div>
  );
}
